'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  FileText,
  Image as ImageIcon,
  Loader2,
  Plus,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { InfiniteCombobox } from '@/components/forms/infinite-combobox';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import {
  ocrTemplatesApi,
  ocrTemplateKeys,
  type OcrTemplate
} from '@/features/ocr-templates';

import {
  a4Dimensions,
  downloadTemplatePagesAsImages,
  downloadTemplatePagesAsPdf,
  generateTemplatePages,
  type GeneratedTemplatePage,
  type TemplatePageSpec
} from '../lib/generate-template-pages';

/** Backend caps the form at 10 sections / 50 pages per section. */
const MAX_SECTIONS = 10;
const MIN_PAGES_PER_SECTION = 1;
const MAX_PAGES_PER_SECTION = 50;
const DPI = 200;

interface DraftSection {
  /** Stable client-side key — sections aren't persisted, so an index suffices. */
  uid: number;
  template_id: string;
  page_count: number;
}

function makeSection(uid: number): DraftSection {
  return { uid, template_id: '', page_count: 3 };
}

/**
 * Builds a synthetic multi-template document by rendering each page to a
 * canvas (one JPEG per page), then either packing them into a PDF (matches
 * the "Upload PDF" flow) or downloading the JPEG sequence (matches the
 * scanner / "Upload Images" flow). Same input shape the Kodak scanner
 * produces so the OCR pipeline runs the realistic raster-image path.
 */
export function TestPdfForm() {
  const t = useTranslations('devTools.testPdf');
  const [sections, setSections] = useState<DraftSection[]>([makeSection(0)]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  const templatesSelect = useInfiniteSelect<OcrTemplate>({
    queryKey: ocrTemplateKeys.lists(),
    queryFn: (params) => ocrTemplatesApi.getAll(params),
    mapOption: useCallback(
      (template: OcrTemplate) => ({
        value: String(template.id),
        label: template.name
      }),
      []
    ),
    params: { is_active: true }
  });

  const totalPages = sections.reduce(
    (sum, section) => sum + (section.template_id ? section.page_count : 0),
    0
  );

  const updateSection = (uid: number, patch: Partial<DraftSection>) => {
    setSections((prev) =>
      prev.map((section) =>
        section.uid === uid ? { ...section, ...patch } : section
      )
    );
  };

  const addSection = () => {
    setSections((prev) => {
      if (prev.length >= MAX_SECTIONS) {
        toast.error(t('maxSectionsReached', { max: MAX_SECTIONS }));
        return prev;
      }
      const nextUid = prev.reduce((max, s) => Math.max(max, s.uid), -1) + 1;
      return [...prev, makeSection(nextUid)];
    });
  };

  const removeSection = (uid: number) => {
    setSections((prev) =>
      prev.length === 1 ? prev : prev.filter((s) => s.uid !== uid)
    );
  };

  /**
   * Hydrate each picked template with its form + custom fields, then build
   * the per-page render specs. Detail responses are cached by id via
   * `ocrTemplatesApi.getById` so repeat sections of the same template don't
   * re-fetch.
   */
  const buildPageSpecs = async (): Promise<TemplatePageSpec[]> => {
    const picked = sections
      .filter((s) => s.template_id !== '')
      .map((s) => ({
        templateId: Number(s.template_id),
        pageCount: Math.min(
          MAX_PAGES_PER_SECTION,
          Math.max(MIN_PAGES_PER_SECTION, Number(s.page_count) || 1)
        )
      }));

    if (picked.length === 0) {
      return [];
    }

    const uniqueIds = Array.from(new Set(picked.map((p) => p.templateId)));
    const detailEntries = await Promise.all(
      uniqueIds.map(async (id) => {
        const response = await ocrTemplatesApi.getById(id);
        return [id, response.data as OcrTemplate] as const;
      })
    );
    const detailsById = new Map<number, OcrTemplate>(detailEntries);

    const specs: TemplatePageSpec[] = [];
    let globalPage = 0;

    for (const { templateId, pageCount } of picked) {
      const template = detailsById.get(templateId);
      if (!template) continue;

      const fieldLabels = collectFieldLabels(template);

      for (let i = 1; i <= pageCount; i++) {
        globalPage++;
        specs.push({
          template,
          sectionPageNumber: i,
          sectionTotalPages: pageCount,
          globalPageNumber: globalPage,
          fieldLabels
        });
      }
    }

    return specs;
  };

  const renderPages = async (): Promise<GeneratedTemplatePage[]> => {
    const specs = await buildPageSpecs();
    if (specs.length === 0) {
      toast.error(t('pickAtLeastOneTemplate'));
      return [];
    }

    const { width, height } = a4Dimensions(DPI);

    setIsGenerating(true);
    setProgress(0);
    try {
      return await generateTemplatePages({
        pages: specs,
        widthPx: width,
        heightPx: height,
        onProgress: (fraction) => setProgress(Math.round(fraction * 100))
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const onDownloadPdf = async () => {
    try {
      const pages = await renderPages();
      if (pages.length === 0) return;
      const filename = `safha-test-document-${stamp()}.pdf`;
      await downloadTemplatePagesAsPdf(pages, filename);
      toast.success(
        t('downloadStarted', { pages: pages.length, sections: countSections() })
      );
    } catch (error) {
      toast.error(t('renderFailed'));
      console.error(error);
    }
  };

  const onDownloadImages = async () => {
    try {
      const pages = await renderPages();
      if (pages.length === 0) return;
      const prefix = `safha-test-${stamp()}`;
      downloadTemplatePagesAsImages(pages, prefix);
      toast.success(t('imagesDownloadStarted', { count: pages.length }));
    } catch (error) {
      toast.error(t('renderFailed'));
      console.error(error);
    }
  };

  const countSections = () =>
    sections.filter((s) => s.template_id !== '').length;

  const isBusy = isGenerating;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        {templatesSelect.isLoading ? (
          <div className='text-muted-foreground flex items-center gap-2 text-sm'>
            <Loader2 className='size-4 animate-spin' />
            {t('loadingTemplates')}
          </div>
        ) : templatesSelect.options.length === 0 && !templatesSelect.search ? (
          <p className='text-muted-foreground text-sm'>{t('noTemplates')}</p>
        ) : (
          <>
            <div className='space-y-3'>
              {sections.map((section, index) => (
                <div
                  key={section.uid}
                  className='flex items-end gap-2 rounded-md border p-3'
                >
                  <div className='flex-1 space-y-1'>
                    <Label className='text-xs'>
                      {t('sectionTemplate', { index: index + 1 })}
                    </Label>
                    <InfiniteCombobox
                      value={section.template_id}
                      onChange={(value) =>
                        updateSection(section.uid, { template_id: value })
                      }
                      infiniteConfig={templatesSelect}
                      placeholder={t('selectTemplate')}
                    />
                  </div>
                  <div className='w-28 space-y-1'>
                    <Label className='text-xs'>{t('pagesLabel')}</Label>
                    <Input
                      type='number'
                      min={MIN_PAGES_PER_SECTION}
                      max={MAX_PAGES_PER_SECTION}
                      value={section.page_count}
                      onChange={(event) =>
                        updateSection(section.uid, {
                          page_count: Number(event.target.value) || 1
                        })
                      }
                    />
                  </div>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() => removeSection(section.uid)}
                    disabled={sections.length === 1 || isBusy}
                    aria-label={t('removeSection')}
                  >
                    <Trash2 className='size-4' />
                  </Button>
                </div>
              ))}
            </div>

            <div className='flex items-center justify-between gap-2'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={addSection}
                disabled={sections.length >= MAX_SECTIONS || isBusy}
              >
                <Plus className='me-2 size-4' />
                {t('addSection')}
              </Button>
              <p className='text-muted-foreground text-xs'>
                {t('totalPages', { count: totalPages })}
              </p>
            </div>

            {isGenerating ? (
              <div className='space-y-1'>
                <Progress value={progress} />
                <p className='text-muted-foreground text-xs'>
                  {t('renderingProgress', { percent: progress })}
                </p>
              </div>
            ) : null}

            <div className='flex flex-wrap justify-end gap-2 pt-2'>
              <Button
                variant='outline'
                onClick={onDownloadImages}
                disabled={isBusy || totalPages === 0}
              >
                {isGenerating ? (
                  <Loader2 className='me-2 size-4 animate-spin' />
                ) : (
                  <ImageIcon className='me-2 size-4' />
                )}
                {t('downloadImagesButton')}
              </Button>
              <Button
                onClick={onDownloadPdf}
                disabled={isBusy || totalPages === 0}
              >
                {isGenerating ? (
                  <Loader2 className='me-2 size-4 animate-spin' />
                ) : (
                  <FileText className='me-2 size-4' />
                )}
                {t('downloadPdfButton')}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Pull every label this template would render — dynamic-form labels first,
 * then custom-field display names. Falls back to placeholders so a barely-
 * configured template still yields a usable page.
 */
function collectFieldLabels(template: OcrTemplate): string[] {
  const labels: string[] = [];

  if (template.dynamic_form?.fields) {
    for (const field of template.dynamic_form.fields) {
      const label = (field as { label?: unknown }).label;
      if (typeof label === 'string' && label.trim() !== '') {
        labels.push(label.trim());
      }
    }
  }

  for (const field of template.custom_fields ?? []) {
    const label = field.display_name ?? field.name ?? '';
    if (typeof label === 'string' && label.trim() !== '') {
      labels.push(label.trim());
    }
  }

  return labels.length === 0 ? ['Field A', 'Field B', 'Field C'] : labels;
}

function stamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
  );
}
